import Fade from "@mui/material/Fade";
import { useToggle } from "@react-hookz/web";
import React from "react";
import { SwitchTransition } from "react-transition-group";
import { useSdkContext } from "scmp-sdk/components/contexts";
import { useNewsletterContext } from "scmp-sdk/components/newsletter/newsletter-widget/contexts";
import type { Props as SignupNewsletterProps } from "scmp-sdk/components/newsletter/newsletter-widget/sign-up-newsletter";
import { ActionComponent } from "scmp-sdk/components/newsletter/newsletter-widget/sign-up-newsletter/enums";
import { useAction } from "scmp-sdk/components/newsletter/newsletter-widget/sign-up-newsletter/hooks";
import IconCheckboxChecked from "scmp-sdk/components/newsletter/newsletter-widget/sign-up-newsletter/icon-checkbox-checked.svg";
import IconCheckboxCheckedOff from "scmp-sdk/components/newsletter/newsletter-widget/sign-up-newsletter/icon-checkbox-off.svg";

import { DescriptionDisplay } from "./description-display";
import {
  ActionAndTermsContainer,
  ActionContainer,
  AgreementCheckboxContainer,
  AgreementContainer,
  AgreementDescription,
  Container,
  ContentContainer,
  CTALink,
  Divider,
  FadeContainer,
  Main,
  MobileContentContainer,
  MobileOnlyNewsletterCTA,
  NewsletterCTAContainer,
  SummaryContainer,
  TabletUpAgreementContainer,
  TabletUpNewsletterCTA,
  TermAndConditionContainer,
  TermAndConditionLink,
  TitleAndDescriptionContainer,
} from "./styles";
import { ThumbnailDisplay } from "./thumbnail-display";
import { TitleDisplay } from "./title-display";

export const SignUpNewsletter: React.FunctionComponent<SignupNewsletterProps> = ({
  isDisableSignupButtonMouseOver = false,
  isShowOverlaySignUp = false,
  onSuccess,
  ...props
}) => {
  const { handleRoutePush, handleTrackGA4 } = useSdkContext();
  const [isAgreedReceiveMarketing, setIsAgreedReceiveMarketing] = useToggle(false);
  const { campaignName } = useNewsletterContext();

  const renderCheckbox = () => {
    if (isAgreedReceiveMarketing) {
      return <IconCheckboxChecked />;
    }
    return <IconCheckboxCheckedOff />;
  };

  const { actionComponent, handleRenderActionComponent } = useAction({
    initialActionComponent: ActionComponent.CombinedSocialAndEmail,
    isAgreedReceiveMarketing,
    isDisableSignupButtonMouseOver,
    isShowOverlaySignUp,
    onSuccess,
  });

  const handleRenderAgreement = () => (
    <>
      <MobileOnlyNewsletterCTA>{handleRenderActionComponent()}</MobileOnlyNewsletterCTA>
      <AgreementContainer>
        <AgreementCheckboxContainer onClick={setIsAgreedReceiveMarketing}>
          {renderCheckbox()}
        </AgreementCheckboxContainer>
        <AgreementDescription>
          {`By submitting, you consent to receiving marketing emails from SCMP. If you don't want these, tick here`}
        </AgreementDescription>
      </AgreementContainer>
      <MobileOnlyNewsletterCTA>
        <TermAndConditionContainer>
          By registering, you agree to our
          <TermAndConditionLink
            href="/terms-conditions"
            onClick={event => {
              event.preventDefault();
              handleTrackGA4({
                action: "click",
                category: "promopopup",
                customized_parameters: {
                  cta_type: "nl_signup",
                  destination_url: "/terms-conditions",
                  internal_campaign: campaignName ?? "",
                  message: "T&C",
                  step: "1",
                },
                subcategory: "",
              });
              void handleRoutePush("/terms-conditions");
            }}
          >
            T&amp;C
          </TermAndConditionLink>{" "}
          and{" "}
          <TermAndConditionLink
            href="/privacy-policy"
            onClick={event => {
              event.preventDefault();
              handleTrackGA4({
                action: "click",
                category: "promopopup",
                customized_parameters: {
                  cta_type: "nl_signup",
                  destination_url: "/privacy-policy",
                  internal_campaign: campaignName ?? "",
                  message: "Privacy Policy",
                  step: "1",
                },
                subcategory: "",
              });
              void handleRoutePush("/privacy-policy");
            }}
          >
            Privacy Policy
          </TermAndConditionLink>
        </TermAndConditionContainer>
      </MobileOnlyNewsletterCTA>
    </>
  );

  const handleRenderTabletUpContent = () => {
    const handleRenderCondition = () => {
      if (
        actionComponent === ActionComponent.OverlaySignUpButton ||
        actionComponent === ActionComponent.SignUpButton
      ) {
        return (
          <>
            <SummaryContainer>
              <ThumbnailDisplay />
            </SummaryContainer>
            <TitleAndDescriptionContainer>
              <TitleDisplay />
              <DescriptionDisplay />
            </TitleAndDescriptionContainer>
          </>
        );
      }
    };
    return (
      <ContentContainer>
        <SwitchTransition mode="out-in">
          <Fade
            key={String(
              actionComponent === ActionComponent.OverlaySignUpButton ||
                actionComponent === ActionComponent.SignUpButton,
            )}
            timeout={500}
          >
            <FadeContainer>{handleRenderCondition()}</FadeContainer>
          </Fade>
        </SwitchTransition>
        <TabletUpNewsletterCTA>{renderNewsletterCTA()}</TabletUpNewsletterCTA>
      </ContentContainer>
    );
  };

  const handleRenderMobileContent = () => {
    const handleRenderCondition = () => {
      if (
        actionComponent === ActionComponent.OverlaySignUpButton ||
        actionComponent === ActionComponent.SignUpButton
      ) {
        return (
          <>
            <TitleDisplay />
            <MobileOnlyNewsletterCTA>{renderNewsletterCTA()}</MobileOnlyNewsletterCTA>
            <Divider />
            <DescriptionDisplay />
            <SummaryContainer>
              <ThumbnailDisplay />
            </SummaryContainer>
          </>
        );
      }
      return handleRenderAgreement();
    };
    return (
      <MobileContentContainer>
        <SwitchTransition mode="out-in">
          <Fade
            key={String(
              actionComponent === ActionComponent.OverlaySignUpButton ||
                actionComponent === ActionComponent.SignUpButton,
            )}
            timeout={500}
          >
            <FadeContainer>{handleRenderCondition()}</FadeContainer>
          </Fade>
        </SwitchTransition>
      </MobileContentContainer>
    );
  };

  const isShowCombinedSocialAndEmail = actionComponent === ActionComponent.CombinedSocialAndEmail;

  const renderNewsletterCTA = () => (
    <NewsletterCTAContainer>
      <SwitchTransition mode="out-in">
        <Fade key={actionComponent} timeout={500}>
          <ActionContainer $isShowCombinedSocialAndEmail={isShowCombinedSocialAndEmail}>
            <ActionAndTermsContainer>
              {handleRenderActionComponent()}
              {isShowCombinedSocialAndEmail && (
                <TabletUpAgreementContainer>{handleRenderAgreement()}</TabletUpAgreementContainer>
              )}
              <TermAndConditionContainer
                $textAlignEnd={actionComponent === ActionComponent.CombinedSocialAndEmail}
              >
                By registering, you agree to our{" "}
                <TermAndConditionLink
                  href="/terms-conditions"
                  onClick={event => {
                    event.preventDefault();
                    handleTrackGA4({
                      action: "click",
                      category: "promopopup",
                      customized_parameters: {
                        cta_type: "nl_signup",
                        destination_url: "/terms-conditions",
                        internal_campaign: campaignName ?? "",
                        message: "T&C",
                        step: "1",
                      },
                      subcategory: "",
                    });
                    void handleRoutePush("/terms-conditions");
                  }}
                >
                  T&amp;C
                </TermAndConditionLink>{" "}
                and{" "}
                <TermAndConditionLink
                  href="/privacy-policy"
                  onClick={event => {
                    event.preventDefault();
                    handleTrackGA4({
                      action: "click",
                      category: "promopopup",
                      customized_parameters: {
                        cta_type: "nl_signup",
                        destination_url: "/privacy-policy",
                        internal_campaign: campaignName ?? "",
                        message: "Privacy Policy",
                        step: "1",
                      },
                      subcategory: "",
                    });
                    void handleRoutePush("/privacy-policy");
                  }}
                >
                  Privacy Policy
                </TermAndConditionLink>
              </TermAndConditionContainer>
            </ActionAndTermsContainer>
            {!isShowCombinedSocialAndEmail && (
              <CTALink
                href="/newsletters"
                onClick={event => {
                  event.preventDefault();
                  void handleRoutePush("/newsletters");
                }}
              >
                FIND OUT MORE
              </CTALink>
            )}
          </ActionContainer>
        </Fade>
      </SwitchTransition>
    </NewsletterCTAContainer>
  );

  return (
    <Container {...props}>
      <Main>
        {handleRenderTabletUpContent()}
        {handleRenderMobileContent()}
      </Main>
    </Container>
  );
};

SignUpNewsletter.displayName = "SignUpNewsletter";
